import React from "react";

/**
 * Style Guide - Professional Design System Preview
 *
 * Design Philosophy:
 * - Black as primary (professional, content-focused)
 * - Blue used sparingly for highlights/interactive elements
 * - Gray for structure and borders
 */

const StyleGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2">YouWo Design System</h1>
          <p className="text-[#64748B] text-lg">Professional, minimal, content-focused</p>
        </div>

        {/* Color Palette */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">Color Palette</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {/* Primary Colors */}
            <div>
              <h3 className="text-sm font-medium text-[#64748B] mb-3 uppercase tracking-wider">Primary (Black)</h3>
              <div className="space-y-2">
                <ColorSwatch color="#0F172A" name="primary-900" label="Text, Buttons" />
                <ColorSwatch color="#1E293B" name="primary-800" label="Headings" />
                <ColorSwatch color="#334155" name="primary-700" label="Icons" />
                <ColorSwatch color="#475569" name="primary-600" label="Secondary text" />
              </div>
            </div>

            {/* Highlight Colors */}
            <div>
              <h3 className="text-sm font-medium text-[#64748B] mb-3 uppercase tracking-wider">Highlight (Blue)</h3>
              <div className="space-y-2">
                <ColorSwatch color="#3B82F6" name="highlight-500" label="Links, Active" />
                <ColorSwatch color="#2563EB" name="highlight-600" label="Hover" />
                <ColorSwatch color="#DBEAFE" name="highlight-100" label="Selection BG" />
                <ColorSwatch color="#EFF6FF" name="highlight-50" label="Subtle BG" />
              </div>
            </div>

            {/* Neutral/Gray */}
            <div>
              <h3 className="text-sm font-medium text-[#64748B] mb-3 uppercase tracking-wider">Neutral (Gray)</h3>
              <div className="space-y-2">
                <ColorSwatch color="#64748B" name="neutral-500" label="Muted text" />
                <ColorSwatch color="#94A3B8" name="neutral-400" label="Placeholder" />
                <ColorSwatch color="#CBD5E1" name="neutral-300" label="Borders" />
                <ColorSwatch color="#E2E8F0" name="neutral-200" label="Dividers" />
              </div>
            </div>

            {/* Background */}
            <div>
              <h3 className="text-sm font-medium text-[#64748B] mb-3 uppercase tracking-wider">Backgrounds</h3>
              <div className="space-y-2">
                <ColorSwatch color="#FFFFFF" name="white" label="Cards" border />
                <ColorSwatch color="#F8FAFC" name="slate-50" label="Page BG" border />
                <ColorSwatch color="#F1F5F9" name="slate-100" label="Hover BG" border />
              </div>
            </div>
          </div>

          {/* Semantic Colors */}
          <div className="mt-8">
            <h3 className="text-sm font-medium text-[#64748B] mb-3 uppercase tracking-wider">Semantic</h3>
            <div className="flex gap-4">
              <ColorSwatch color="#22C55E" name="success" label="Success" />
              <ColorSwatch color="#F59E0B" name="warning" label="Warning" />
              <ColorSwatch color="#EF4444" name="error" label="Error" />
              <ColorSwatch color="#3B82F6" name="info" label="Info" />
            </div>
          </div>
        </section>

        {/* Typography */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">Typography</h2>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-8">
            <div className="space-y-6">
              <div>
                <span className="text-xs text-[#94A3B8] uppercase tracking-wider">H1 - Page Title</span>
                <h1 className="text-4xl font-bold text-[#0F172A]">Your AI Space</h1>
              </div>
              <div>
                <span className="text-xs text-[#94A3B8] uppercase tracking-wider">H2 - Section Title</span>
                <h2 className="text-2xl font-semibold text-[#0F172A]">Recent Notes</h2>
              </div>
              <div>
                <span className="text-xs text-[#94A3B8] uppercase tracking-wider">H3 - Card Title</span>
                <h3 className="text-lg font-medium text-[#1E293B]">Meeting Notes - Q4 Planning</h3>
              </div>
              <div>
                <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Body</span>
                <p className="text-base text-[#334155]">This is body text. It should be easy to read and not too dark or too light.</p>
              </div>
              <div>
                <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Muted / Secondary</span>
                <p className="text-sm text-[#64748B]">Last edited 2 hours ago · 3 sources</p>
              </div>
              <div>
                <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Link</span>
                <a href="#" className="text-[#3B82F6] hover:text-[#2563EB] hover:underline">View all notes →</a>
              </div>
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">Buttons</h2>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-8">

            {/* Primary Buttons */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-[#64748B] mb-4 uppercase tracking-wider">Primary (Black - Main Actions)</h3>
              <div className="flex flex-wrap gap-4">
                <button className="px-6 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium hover:bg-[#1E293B] transition-colors">
                  Create Note
                </button>
                <button className="px-6 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium hover:bg-[#1E293B] transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Recording
                </button>
                <button className="px-6 py-2.5 bg-[#334155] text-white rounded-lg font-medium cursor-not-allowed opacity-60">
                  Disabled
                </button>
              </div>
            </div>

            {/* Secondary Buttons */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-[#64748B] mb-4 uppercase tracking-wider">Secondary (Outlined)</h3>
              <div className="flex flex-wrap gap-4">
                <button className="px-6 py-2.5 border border-[#CBD5E1] text-[#334155] rounded-lg font-medium hover:border-[#94A3B8] hover:bg-[#F8FAFC] transition-colors">
                  Cancel
                </button>
                <button className="px-6 py-2.5 border border-[#CBD5E1] text-[#334155] rounded-lg font-medium hover:border-[#94A3B8] hover:bg-[#F8FAFC] transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Export
                </button>
              </div>
            </div>

            {/* Ghost/Text Buttons */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-[#64748B] mb-4 uppercase tracking-wider">Ghost / Text</h3>
              <div className="flex flex-wrap gap-4">
                <button className="px-4 py-2 text-[#334155] rounded-lg font-medium hover:bg-[#F1F5F9] transition-colors">
                  View All
                </button>
                <button className="px-4 py-2 text-[#3B82F6] rounded-lg font-medium hover:bg-[#EFF6FF] transition-colors">
                  Learn More
                </button>
              </div>
            </div>

            {/* Highlight Buttons (Use Sparingly) */}
            <div>
              <h3 className="text-sm font-medium text-[#64748B] mb-4 uppercase tracking-wider">Highlight (Blue - Use Sparingly)</h3>
              <p className="text-sm text-[#94A3B8] mb-4">Only for primary CTAs on landing pages or critical actions</p>
              <div className="flex flex-wrap gap-4">
                <button className="px-6 py-2.5 bg-[#3B82F6] text-white rounded-lg font-medium hover:bg-[#2563EB] transition-colors">
                  Start Free Trial
                </button>
                <button className="px-6 py-2.5 border border-[#3B82F6] text-[#3B82F6] rounded-lg font-medium hover:bg-[#EFF6FF] transition-colors">
                  Watch Demo
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Cards & Lists */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">Cards & Lists</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Note Card */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 hover:border-[#CBD5E1] hover:shadow-sm transition-all cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#64748B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-[#0F172A]">Q4 Planning Meeting</h3>
                    <p className="text-sm text-[#64748B]">2 hours ago</p>
                  </div>
                </div>
                <button className="text-[#94A3B8] hover:text-[#64748B]">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-[#475569] mb-3 line-clamp-2">
                Discussed roadmap priorities, budget allocation, and team expansion plans for next quarter...
              </p>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-[#F1F5F9] text-[#64748B] text-xs rounded-md">Recording</span>
                <span className="px-2 py-1 bg-[#F1F5F9] text-[#64748B] text-xs rounded-md">3 sources</span>
              </div>
            </div>

            {/* Note Card - Selected State */}
            <div className="bg-white rounded-xl border-2 border-[#3B82F6] p-5 shadow-sm cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-[#0F172A]">Product Demo Recording</h3>
                    <p className="text-sm text-[#3B82F6]">Selected</p>
                  </div>
                </div>
                <div className="w-5 h-5 bg-[#3B82F6] rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-sm text-[#475569] mb-3 line-clamp-2">
                Full walkthrough of the new AI features including real-time transcription and smart summaries...
              </p>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-[#DBEAFE] text-[#3B82F6] text-xs rounded-md font-medium">Video</span>
                <span className="px-2 py-1 bg-[#F1F5F9] text-[#64748B] text-xs rounded-md">45 min</span>
              </div>
            </div>
          </div>
        </section>

        {/* List Items */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">List Items</h2>
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            {/* List Item - Default */}
            <div className="px-5 py-4 border-b border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#64748B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A]">Research_Paper_v2.pdf</p>
                    <p className="text-sm text-[#64748B]">24 pages · Added yesterday</p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#94A3B8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            {/* List Item - Active/Selected */}
            <div className="px-5 py-4 border-b border-[#E2E8F0] bg-[#EFF6FF] cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#DBEAFE] rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A]">Team Standup Recording</p>
                    <p className="text-sm text-[#3B82F6]">Currently playing</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text--[#3B82F6] font-medium">12:34</span>
                  <svg className="w-5 h-5 text-[#3B82F6]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* List Item - With Badge */}
            <div className="px-5 py-4 hover:bg-[#F8FAFC] cursor-pointer transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#64748B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A]">YouTube: AI in 2025</p>
                    <p className="text-sm text-[#64748B]">1:23:45 · Summarized</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 bg-[#22C55E] text-white text-xs rounded-full font-medium">New</span>
              </div>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">Form Elements</h2>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Input Default */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">Default Input</label>
                <input
                  type="text"
                  placeholder="Enter note title..."
                  className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-lg text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#DBEAFE] transition-colors"
                />
              </div>

              {/* Input Focused */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">Focused Input</label>
                <input
                  type="text"
                  value="My Meeting Notes"
                  className="w-full px-4 py-2.5 border-2 border-[#3B82F6] rounded-lg text-[#0F172A] focus:outline-none ring-2 ring-[#DBEAFE]"
                  readOnly
                />
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">Search</label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search notes..."
                    className="w-full pl-10 pr-4 py-2.5 border border-[#CBD5E1] rounded-lg text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#DBEAFE]"
                  />
                </div>
              </div>

              {/* Select */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">Select</label>
                <select className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-lg text-[#0F172A] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#DBEAFE] bg-white">
                  <option>All Notes</option>
                  <option>Recordings</option>
                  <option>PDFs</option>
                  <option>Videos</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison: Old vs New */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">Comparison: Old vs New</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Old Style */}
            <div className="p-6 rounded-xl border-2 border-dashed border-[#EF4444] bg-[#FEF2F2]">
              <h3 className="text-lg font-medium text-[#EF4444] mb-4">❌ Old (Too Much Blue)</h3>
              <button className="w-full mb-3 px-6 py-2.5 bg-[#4ea4f5] text-white rounded-lg font-medium">
                Create Note
              </button>
              <button className="w-full mb-3 px-6 py-2.5 bg-[#3B82F6] text-white rounded-lg font-medium">
                Export PDF
              </button>
              <button className="w-full px-6 py-2.5 bg-[#2563EB] text-white rounded-lg font-medium">
                Share
              </button>
              <p className="text-sm text-[#64748B] mt-4">Everything is blue - nothing stands out</p>
            </div>

            {/* New Style */}
            <div className="p-6 rounded-xl border-2 border-[#22C55E] bg-[#F0FDF4]">
              <h3 className="text-lg font-medium text-[#22C55E] mb-4">✓ New (Black Primary, Blue Accent)</h3>
              <button className="w-full mb-3 px-6 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium hover:bg-[#1E293B]">
                Create Note
              </button>
              <button className="w-full mb-3 px-6 py-2.5 border border-[#CBD5E1] text-[#334155] rounded-lg font-medium hover:bg-[#F8FAFC]">
                Export PDF
              </button>
              <button className="w-full px-6 py-2.5 text-[#3B82F6] rounded-lg font-medium hover:bg-[#EFF6FF]">
                Share
              </button>
              <p className="text-sm text-[#64748B] mt-4">Clear hierarchy - primary action stands out</p>
            </div>
          </div>
        </section>

        {/* Design Principles */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-[#0F172A] mb-6">Design Principles</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
              <div className="w-12 h-12 bg-[#0F172A] rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">1</span>
              </div>
              <h3 className="font-semibold text-[#0F172A] mb-2">Black is Primary</h3>
              <p className="text-sm text-[#64748B]">Use black/dark slate for main actions, text, and important UI. It's professional and content-focused.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
              <div className="w-12 h-12 bg-[#3B82F6] rounded-xl flex items-center justify-center mb-4">
                <span className="text-white text-xl">2</span>
              </div>
              <h3 className="font-semibold text-[#0F172A] mb-2">Blue is Highlight</h3>
              <p className="text-sm text-[#64748B]">Reserve blue for links, active states, selections, and landing page CTAs. Use sparingly to make it meaningful.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
              <div className="w-12 h-12 bg-[#F1F5F9] rounded-xl flex items-center justify-center mb-4 border border-[#E2E8F0]">
                <span className="text-[#64748B] text-xl">3</span>
              </div>
              <h3 className="font-semibold text-[#0F172A] mb-2">Gray is Structure</h3>
              <p className="text-sm text-[#64748B]">Use gray for borders, dividers, backgrounds, and secondary text. It provides structure without distraction.</p>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

// Color Swatch Component
const ColorSwatch: React.FC<{ color: string; name: string; label: string; border?: boolean }> = ({
  color,
  name,
  label,
  border
}) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-10 h-10 rounded-lg ${border ? 'border border-[#E2E8F0]' : ''}`}
      style={{ backgroundColor: color }}
    />
    <div>
      <p className="text-sm font-mono text-[#334155]">{color}</p>
      <p className="text-xs text-[#94A3B8]">{label}</p>
    </div>
  </div>
);

export default StyleGuide;
